import { Box, CircularProgress, TextField, Tooltip, Typography, useTheme, TableRow, TableCell, Theme } from "@mui/material"
import { BlackDownloadIcon, SearchIcon } from "../../assets/SvgIcons"
import CustomTable from "../investorOverview/common/customTable"
import { AmcTableBody, AmctableHeaderData } from "../investorOverview/mutualFund/common/Table"
import { Order, SortingObj } from "../investorOverview/types"
import { useSelector } from "react-redux"
import { RootStateType } from "../../redux-store/reducers"
import { formatCurrency ,getValueColor} from "../../utils/utilityFunctions"
import {AmcPerformanceAnalysisData} from "../../redux-store/types/analytics"


type CategoriesTableProps = {
    title: string
    tableData: any
    isLoading: boolean
    filters?: boolean
    handleTableSorting: (orderBy: string, order: Order) => void
    sortingObj: SortingObj,
    setSortingObj: ({ order, orderBy }: SortingObj) => void
    amcDownload: () => void
    amcDownloadLoader?: boolean;
}


export default function AMCPerformanceAnalysis(props: CategoriesTableProps): JSX.Element {
    const { title, tableData, isLoading, filters = true, handleTableSorting, sortingObj, setSortingObj, amcDownload, amcDownloadLoader } = props
    const { palette, typography } = useTheme()


    return (
        <Box sx={{
            padding:{ xs:'25px 15px 25px 10px', sm:'25px 30px 45px 30px'},
            background: palette.common.white,
            borderRadius: '20px'
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }} >
                <Typography variant="h2" color={palette.text.primary} sx={{ fontSize: { xs: '15px'},ml:{xs:2,sm:0}}}>
                    {title}
                </Typography>
                {filters && (
                    <Tooltip
                        slotProps={{
                            tooltip: {
                                sx: {
                                    margin: "0px !important",
                                    backgroundColor: "transparent",
                                    wordWrap: 'normal'
                                }
                            }
                        }}
                        title={
                            <div
                                style={{
                                    borderRadius: '10px', backgroundColor: '#0F131F', padding: '10px'
                                }}
                            >
                                <span style={{ ...typography.subtitle_500, fontFamily: typography.fontFamily }}>Download AMC Performance analysis report</span>
                            </div>
                        }
                    >
                        {isLoading ? <></> : <Box display={'flex'} gap={'10px'}
                            onClick={amcDownload}
                        >
                            {amcDownloadLoader ? <CircularProgress size={20} /> : <Box component={'button'} sx={{ cursor: 'pointer', border: 'none', display: 'flex', gap: '10px', background: 'transparent', alignItems: 'center' }}>
                                <BlackDownloadIcon />
                                <Typography
                                    color={palette.text.primary}
                                    sx={{ ...typography.body_500 }}

                                >Download</Typography>
                            </Box>}
                        </Box>
                        }
                    </Tooltip>

                )}

            </Box>
            <Box mt={'20px'}>
                <CustomTable
                    loadingRowsNum={5}
                    isLoading={isLoading}
                    tableBody={
                        <AmcTableBody
                            tableBodyData={tableData || []}
                            handleCheckboxClick={(id: number) => console.log("Checkbox clicked", id)}
                        />
                    }
                    tableData={tableData || []}
                    tableBodyLoaderRowCustomStyles={{
                        '& td:first-of-type': {
                            paddingLeft: "0px"
                        },
                    }}
                    tableHeaderData={AmctableHeaderData}
                    tableHeadRowCustomStyles={{
                        '& th:first-of-type': {
                            paddingLeft: "0px"
                        }
                    }}
                    handleSort={handleTableSorting}
                    tableBodyCustomStyles={{
                        '& tr:last-child td': {
                            border: 0
                        }
                    }}
                    order={sortingObj.order}
                    orderBy={sortingObj.orderBy}
                    tableDataCount={tableData?.length || 0}
                    isPaginationVisible={false}
                    manualResponsiveBody={true}
                    hideResponsiveHeader={true}
                    ManualResponsiveBodyComponent={
                        <MobileResponsiveBody
                            // selectedHeaderKey={sortingObj.orderBy}
                            data={tableData || []}
                        />
                    }
                />
            </Box>
        </Box>
    )
}

// Custom Mobile Responsive Body Component for Dashboard AMC Performance Analysis
function MobileResponsiveBody({
    data
  }: {
    data: AmcPerformanceAnalysisData[]
  }): JSX.Element {
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);
  
    function calculatePercentage(x: string | number, y: string | number): string {
      x = Number(x);
      y = Number(y);
  
      if (!Number.isFinite(x) || !Number.isFinite(y) || y === 0) {
        return "-";
      }
  
      return `${Math.floor((x / y) * 100)}%`;
    }
  
    const getFormattedValue = (item: AmcPerformanceAnalysisData, key: string) => {
      switch (key) {
        case "holding":
        case "equity":
        case "debt":
        case "hybrid": {
          const value = Number(item?.[key] ?? 0);
          return value ? `${value.toFixed(2)}%` : "0"; 
        }
  
        case "investedValue":
          return (item?.investedValue ?? 0) < 0
            ? `- ${formatCurrency({ value: Math.abs(item?.investedValue ?? 0), locale, currencyCode })}`
            : `${formatCurrency({ value: item?.investedValue ?? 0, locale, currencyCode })}`;
  
        case "marketValue":
          return (item?.marketValue ?? 0) < 0
            ? `- ${formatCurrency({ value: Math.abs(item?.marketValue ?? 0), locale, currencyCode })}`
            : `${formatCurrency({ value: item?.marketValue ?? 0, locale, currencyCode })}`;
  
        case "unRelease": {
          const unRealisedGainLossPerc = calculatePercentage(item?.unRelease, item?.investedValue);
          return (item?.unRelease ?? 0) < 0
            ? `- ${formatCurrency({ value: Math.abs(item?.unRelease ?? 0), locale, currencyCode })} (${unRealisedGainLossPerc})`
            : `${formatCurrency({ value: item?.unRelease ?? 0, locale, currencyCode })} (${unRealisedGainLossPerc})`;
        }
  
        case "irr": {
          const value = Number(item?.irr ?? 0);
          return value ? `${value.toFixed(2)}%` : "0"; 
        }
  
        default:
          return item?.[key as keyof AmcPerformanceAnalysisData] ?? "-";
      }
    };
  
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        {data.map((item, index) => (
          <Box
            key={`${item?.amcname ?? ""}-${index}`}
            sx={(theme: Theme) => ({
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "12px",
              padding: "16px",
              backgroundColor: theme.palette.background.paper,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            })}
          >
            {/* AMC Name */}
            <Typography
              variant="body_600"
              sx={(theme: Theme) => ({
                color: theme.palette.text.primary,
                fontSize: "16px",
                marginBottom: "8px",
              })}
            >
              {item?.amcname ?? "-"}
            </Typography>
  
            {/* Details */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr", 
                rowGap: "12px",
              }}
            >
              {AmctableHeaderData
                .filter(header => header.id !== "amcname")
                .map(header => (
                  <Box
                    key={header.id}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={(theme: Theme) => ({
                        color: theme.palette.text.secondary,
                        fontSize: "12px",
                        lineHeight: "16px",
                      })}
                    >
                      {header.label}
                    </Typography>
                    <Typography
                      variant="body_500"
                      sx={(theme: Theme) => ({
                        color: getValueColor(
                          header.id,
                          Number(item?.[header.id as keyof AmcPerformanceAnalysisData] ?? 0),
                          theme
                        ),
                        fontSize: "14px",
                        lineHeight: "20px",
                      })}
                    >
                      {getFormattedValue(item, header.id)}
                    </Typography>
                  </Box>
                ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }
  


